import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import timezoneService from "../services/timezoneService";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";
const USER_ID = process.env.REACT_APP_USER_ID || "mario";

const ChatInterface = React.forwardRef(
  (
    { currentLocation, locationService, onDeviceReload, onQuickCommand },
    ref
  ) => {
    const [messages, setMessages] = useState([
      {
        id: 1,
        type: "bot",
        content:
          "Hello! I'm Arya, your personal assistant. How can I help you today?",
        timestamp: new Date(),
      },
    ]);
    const [inputMessage, setInputMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-scroll to bottom when new messages are added
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
      scrollToBottom();
    }, [messages]);

    // Focus input when component mounts
    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    const sendMessage = useCallback(
      async (messageText = null) => {
        const textToSend = messageText || inputMessage;
        if (
          !textToSend ||
          typeof textToSend !== "string" ||
          !textToSend.trim() ||
          loading
        )
          return;

        const userMessage = {
          id: Date.now(),
          type: "user",
          content: textToSend.trim(),
          timestamp: new Date(),
        };

        // Add user message immediately
        setMessages((prev) => [...prev, userMessage]);
        setInputMessage("");
        setLoading(true);

        try {
          // Include location and timezone context if available
          const context = {
            user_id: USER_ID,
            location: "home",
            conversation_history: messages.slice(-10).map((msg) => ({
              role: msg.type === "user" ? "user" : "assistant",
              content: msg.content,
            })),
            // Add timezone information
            ...timezoneService.getApiTimezoneParams(),
          };

          if (currentLocation) {
            context.current_location = {
              latitude: currentLocation.latitude,
              longitude: currentLocation.longitude,
              name: await locationService.getLocationName(
                currentLocation.latitude,
                currentLocation.longitude
              ),
            };
          }

          const response = await axios.post(`${API_BASE_URL}/api/v1/commands`, {
            text: userMessage.content,
            context: context,
          });

          const botMessage = {
            id: Date.now() + 1,
            type: "bot",
            content:
              response.data.text || "I processed your request successfully.",
            timestamp: new Date(),
          };

          setMessages((prev) => [...prev, botMessage]);

          // Reload devices to show any changes if callback is provided
          if (onDeviceReload) {
            setTimeout(onDeviceReload, 1000);
          }
        } catch (error) {
          const errorMessage = {
            id: Date.now() + 1,
            type: "bot",
            content: `Sorry, I encountered an error: ${
              error.response?.data?.error || error.message
            }`,
            timestamp: new Date(),
            isError: true,
          };

          setMessages((prev) => [...prev, errorMessage]);
        } finally {
          setLoading(false);
        }
      },
      [
        inputMessage,
        loading,
        currentLocation,
        locationService,
        onDeviceReload,
        messages,
      ]
    );

    const handleKeyPress = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    };

    const clearChat = () => {
      setMessages([
        {
          id: 1,
          type: "bot",
          content:
            "Hello! I'm Arya, your personal assistant. How can I help you today?",
          timestamp: new Date(),
        },
      ]);
    };

    const sendQuickCommand = useCallback(
      (command) => {
        if (!command || typeof command !== "string") {
          return;
        }
        // Ensure we have a valid string
        const validCommand = String(command).trim();
        if (!validCommand) {
          return;
        }
        // Send the command directly without setting input state
        sendMessage(validCommand);
      },
      [sendMessage]
    );

    // Expose sendQuickCommand method to parent component
    React.useImperativeHandle(ref, () => ({
      sendQuickCommand,
    }));

    // Listen for quick commands from parent
    React.useEffect(() => {
      if (onQuickCommand) {
        onQuickCommand(sendQuickCommand);
      }
    }, [onQuickCommand]);

    const formatTime = (timestamp) => {
      return timestamp.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "600px",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#fafafa",
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            backgroundColor: "#fff",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ bgcolor: "#1976d2", width: 32, height: 32 }}>
              <BotIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Arya Assistant
            </Typography>
          </Box>
          <IconButton onClick={clearChat} size="small" title="Clear chat">
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Messages Container */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: "flex",
                justifyContent:
                  message.type === "user" ? "flex-end" : "flex-start",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {message.type === "bot" && (
                <Avatar
                  sx={{ bgcolor: "#1976d2", width: 32, height: 32, mt: 0.5 }}
                >
                  <BotIcon />
                </Avatar>
              )}

              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  maxWidth: "70%",
                  backgroundColor: message.type === "user" ? "#1976d2" : "#fff",
                  color: message.type === "user" ? "#fff" : "#000",
                  borderRadius:
                    message.type === "user"
                      ? "18px 18px 4px 18px"
                      : "18px 18px 18px 4px",
                  border: message.isError ? "1px solid #f44336" : "none",
                  backgroundColor: message.isError
                    ? "#ffebee"
                    : message.type === "user"
                    ? "#1976d2"
                    : "#fff",
                  color: message.isError
                    ? "#d32f2f"
                    : message.type === "user"
                    ? "#fff"
                    : "#000",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {message.content}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1,
                    opacity: 0.7,
                    fontSize: "0.75rem",
                  }}
                >
                  {formatTime(message.timestamp)}
                </Typography>
              </Paper>

              {message.type === "user" && (
                <Avatar
                  sx={{ bgcolor: "#757575", width: 32, height: 32, mt: 0.5 }}
                >
                  <PersonIcon />
                </Avatar>
              )}
            </Box>
          ))}

          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Avatar
                sx={{ bgcolor: "#1976d2", width: 32, height: 32, mt: 0.5 }}
              >
                <BotIcon />
              </Avatar>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  backgroundColor: "#fff",
                  borderRadius: "18px 18px 18px 4px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Arya is thinking...
                </Typography>
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#fff",
            display: "flex",
            gap: 1,
            alignItems: "flex-end",
          }}
        >
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            variant="outlined"
            placeholder="Type your message here... (Press Enter to send, Shift+Enter for new line)"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
              },
            }}
          />
          <Button
            variant="contained"
            onClick={sendMessage}
            disabled={loading || !inputMessage || !inputMessage.trim()}
            sx={{
              minWidth: "auto",
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </Button>
        </Box>
      </Box>
    );
  }
);

ChatInterface.displayName = "ChatInterface";

export default ChatInterface;
